import React from "react";
import { AxiosRequestConfig } from "axios";
import Select, { components, OptionProps, MenuListProps, SingleValueProps } from "react-select";

import { setDealID, setDocs, setAllDocs, resetState } from "redux/features/CreateOrder";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import {
  GetAllDealRequestType,
  getAllDeals,
} from "service/apis/CreateOrderapi";

interface Deal {
  id: string | number;
  status: string;
  utilization_status: string;
  opportunity_id?: string | number;
  contacts_id?: string | number;
  currency?: string;
  amount?: number;
}

interface DealOption {
  value: string | number;
  label: string;
  dealData: Deal;
}

const Step1: React.FC = () => {
  const [dealOptions, setDealOptions] = React.useState<DealOption[]>([]);
  const [dealDetails, setDealDetails] = React.useState<Deal[]>([]);

  const created_by = useAppSelector((state) => state.CreateOrder.created_by);
  const showerror = useAppSelector((state) => state.CreateOrder.showerror);
  const deal_id = useAppSelector((state) => state.CreateOrder.deal_id);

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async (): Promise<void> => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      const payload: AxiosRequestConfig<GetAllDealRequestType> = {
        headers: {
          Authorization: `Bearer ${JSON.parse(authToken)}`,
        },
      };

      const response = await getAllDeals(payload);
      const allDeals: Deal[] = response.data;

      const filteredDeals = allDeals.filter(
        (deal) =>
          deal.status === "confirmed" &&
          deal.utilization_status === "unutilized",
      );

      setDealDetails(filteredDeals);
      
      // Create options for react-select with deal data
      const options = filteredDeals
        .sort((a, b) => Number(b.id) - Number(a.id))
        .map((deal) => ({
          value: deal.id,
          label: `D-${deal.id}`,
          dealData: deal,
        }));
      
      setDealOptions(options);
    } catch (error) {
      console.error("Error fetching deals:", error);
    }
  };

  // Custom Option component for table-like display
  const CustomOption: React.FC<OptionProps<DealOption>> = (props) => {
    const { data, isSelected, isFocused } = props;
    
    return (
      <div
        {...props.innerProps}
        className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
          isFocused ? 'bg-gray-50' : 'bg-white'
        } ${isSelected ? 'bg-blue-50' : ''}`}
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="text-sm text-gray-900 font-medium">
            D-{data.dealData.id}
          </div>
          <div className="text-sm text-gray-600">
            {data.dealData.currency || 'USD'} {data.dealData.amount?.toLocaleString() || '0'}
          </div>
        </div>
      </div>
    );
  };

  // Custom MenuList to add table header
  const CustomMenuList: React.FC<MenuListProps<DealOption>> = (props) => {
    return (
      <div>
        {/* Table Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 sticky top-0 z-10">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-sm font-medium text-gray-600">Deal ID</div>
            <div className="text-sm font-medium text-gray-600">FCY Amount</div>
          </div>
        </div>
        {/* Options */}
        <components.MenuList {...props}>
          {props.children}
        </components.MenuList>
      </div>
    );
  };

  // Custom SingleValue to show selected value properly
  const CustomSingleValue: React.FC<SingleValueProps<DealOption>> = (props) => {
    return (
      <components.SingleValue {...props}>
        <span className="text-black-900">D-{props.data.dealData.id}</span>
      </components.SingleValue>
    );
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#ffffff',
      border: `1px solid ${(deal_id === "" && showerror) ? '#ef4444' : 'rgba(0, 0, 0, 0.3)'}`,
      borderRadius: '30px',
      height: '52px',
      paddingLeft: '40px',
      paddingRight: '13px',
      fontSize: '15px',
      letterSpacing: '0.5px',
      boxShadow: 'none',
      '&:hover': {
        border: `1px solid ${(deal_id === "" && showerror) ? '#ef4444' : 'rgba(0, 0, 0, 0.3)'}`,
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: '0',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#000',
      '&:hover': {
        color: '#000',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    }),
    menuList: (provided: any) => ({
      ...provided,
      padding: '0',
      maxHeight: '320px',
    }),
    option: () => ({
      // We're handling styles in CustomOption component
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'rgba(0, 0, 0, 0.6)',
    }),
  };

  // Find selected option
  const selectedOption = dealOptions.find(option => option.value === deal_id) || null;

  return (
    <>
      <div className="bg-white-A700 rounded-lg flex md:flex-1 flex-col gap-5 md:h-auto items-start justify-start md:px-5 w-full md:w-full">
        <div className="flex flex-col gap-[15px] items-start justify-start w-[377px] sm:w-full">
          <div className="flex flex-col relative font-inter gap-2.5 items-start justify-center w-full">
            <Select<DealOption>
              options={dealOptions}
              value={selectedOption}
              onChange={(selectedOption) => {
                // dispatch(resetState());
                if (selectedOption) {
                  dispatch(setDealID(selectedOption.value));
                  dispatch(setDocs({ filename: "", file: null }));
                  dispatch(setAllDocs([]));
                  // You can also dispatch other actions here
                  // dispatch(setOpportunityId(selectedOption.dealData.opportunity_id));
                  // dispatch(setContactId(selectedOption.dealData.contacts_id));
                }
              }}
              components={{
                Option: CustomOption,
                MenuList: CustomMenuList,
                SingleValue: CustomSingleValue,
              }}
              styles={customStyles}
              isSearchable
              placeholder="Search and Select Deal"
              noOptionsMessage={() => "No deals found"}
              className="w-full"
              classNamePrefix="react-select"
            />
            
            {/* Search Icon */}
            <svg
              className="absolute left-5 top-[22px] pointer-events-none"
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.6875 12.5689L9.38349 9.2649C10.1775 8.31172 10.5734 7.08914 10.4889 5.85148C10.4044 4.61382 9.84604 3.45638 8.92991 2.61993C8.01378 1.78349 6.81044 1.33244 5.57022 1.36062C4.33 1.3888 3.14839 1.89404 2.2712 2.77123C1.39401 3.64842 0.88877 4.83003 0.86059 6.07025C0.83241 7.31047 1.28346 8.51381 2.1199 9.42994C2.95635 10.3461 4.11379 10.9045 5.35145 10.9889C6.58911 11.0734 7.81169 10.6775 8.76486 9.88352L12.0689 13.1875L12.6875 12.5689ZM1.74999 6.18752C1.74999 5.40876 1.98092 4.64748 2.41358 3.99996C2.84624 3.35244 3.46119 2.84776 4.18067 2.54974C4.90016 2.25172 5.69186 2.17375 6.45566 2.32568C7.21946 2.47761 7.92105 2.85262 8.47172 3.40329C9.02239 3.95396 9.3974 4.65555 9.54933 5.41935C9.70126 6.18315 9.62329 6.97485 9.32527 7.69434C9.02725 8.41382 8.52257 9.02877 7.87505 9.46143C7.22753 9.89409 6.46625 10.125 5.68749 10.125C4.64355 10.1239 3.64271 9.70865 2.90454 8.97047C2.16636 8.2323 1.75115 7.23145 1.74999 6.18752Z"
                fill="black"
              />
            </svg>

            {/* Error Message */}
            {deal_id === "" && showerror && (
              <div className="text-red-500 text-sm mt-1">
                Deal is required
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Step1;